import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import FAQ from '../components/FAQ';
import Icon from '../components/Icon';
import Seo from '../components/Seo';
import faqData from '../data/faq';
import jslLogo from '../assets/jcl.png';
import jslLogoWebp from '../assets/jcl.webp';
import microscope from '../assets/microscope.jpg';
import microscopeWebp from '../assets/microscope.webp';
import microscopeWebpMobile from '../assets/microscope-mobile.webp';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqData.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer
    }
  }))
};

function Home() {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const onHeroMove = useCallback((event) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.matchMedia('(pointer: coarse)').matches) return;
    const box = event.currentTarget.getBoundingClientRect();
    setTilt({
      x: (event.clientX - box.left) / box.width - 0.5,
      y: (event.clientY - box.top) / box.height - 0.5
    });
  }, []);

  const onHeroLeave = useCallback(() => setTilt({ x: 0, y: 0 }), []);

  return (
    <div className="home-page">
      <Seo
        title="GI & Anatomic Pathology Lab"
        brandFirst
        path="/"
        description="GI-focused anatomic pathology laboratory in Delray Beach, Florida, since 2000: surgical pathology, cytopathology, dermatopathology and immunohistochemistry."
        jsonLd={faqSchema}
      />
      <section
        className="hero"
        onMouseMove={onHeroMove}
        onMouseLeave={onHeroLeave}
        style={{ '--mx': String(tilt.x), '--my': String(tilt.y) }}
      >
        <div className="hero-media">
          <picture>
            {/* .hero-media drops to a 240px-tall banner at this same breakpoint
                (see index.css), so a 700px-wide source is plenty there —
                the full 1280px source was being downloaded on mobile too. */}
            <source srcSet={microscopeWebpMobile} type="image/webp" media="(max-width: 768px)" />
            <source srcSet={microscopeWebp} type="image/webp" />
            <img
              className="hero-photo"
              fetchpriority="high"
              src={microscope}
              width="1280"
              height="853"
              alt="Microscope used for anatomic pathology diagnosis"
            />
          </picture>
          <div className="hero-lamp" aria-hidden="true" />
        </div>
        <div className="hero-panel">
          <h1>GI &amp; Anatomic Pathology for Florida</h1>
          <p>Boca-Delray Pathology Laboratories is a Florida pathology laboratory specializing in gastrointestinal pathology, with surgical pathology, cytopathology, and dermatopathology for clinicians across the state. Our board-certified pathologists and technicians deliver accurate, well-documented diagnoses that support confident treatment decisions.</p>
          <div className="hero-buttons">
            <Link to="/team" className="cta-button">Meet our Team</Link>
            <Link to="/services" className="cta-button secondary">Our Services</Link>
          </div>
          <div className="hero-meta">
            <div className="hero-since">
              <strong>Since 2000</strong>
              <span>Florida’s premier GI pathology group, accredited by The Joint Commission.</span>
            </div>
            <a
              className="hero-seal"
              rel="noopener noreferrer"
              href="https://www.jointcommission.org/"
              target="_blank"
            >
              <picture>
                <source srcSet={jslLogoWebp} type="image/webp" />
                <img
                  src={jslLogo}
                  width="150"
                  height="150"
                  alt="The Joint Commission Gold Seal of Approval"
                />
              </picture>
            </a>
          </div>
        </div>
      </section>

      <section className="collaboration gi-specialty">
        <p className="section-label">Our Specialty</p>
        <h2>Gastrointestinal Pathology for Florida Practices</h2>
        <div className="feature-grid">
          <div className="feature-item">
            <Icon name="microscope" className="card-icon" />
            <h3>Esophagus &amp; Stomach</h3>
            <p>Barrett’s esophagus surveillance and dysplasia grading, eosinophilic esophagitis, H. pylori gastritis, and intestinal metaplasia.</p>
          </div>
          <div className="feature-item">
            <Icon name="slide" className="card-icon" />
            <h3>Small Bowel &amp; Colon</h3>
            <p>Celiac disease, colorectal polyp classification, inflammatory bowel disease, and microscopic colitis.</p>
          </div>
          <div className="feature-item">
            <Icon name="truck" className="card-icon" />
            <h3>Built for GI Practices</h3>
            <p>From our Delray Beach laboratory in South Florida, we work with GI practices, endoscopy and surgery centers, and hospitals across Florida, with routine results typically in 24–48 hours. <Link to="/gi-pathology" className="learn-more">See GI pathology</Link></p>
          </div>
        </div>
      </section>

      <section id="services" className="anatomic-pathology">
        <p className="section-label">Our Services</p>
        <h2>Our Expertise in Anatomic Pathology</h2>
        <div className="service-grid">
          <div className="service-item">
            <Icon name="slide" className="card-icon" />
            <h3>Surgical Pathology</h3>
            <p>Board-certified pathologists examine surgical specimens and report findings that inform treatment decisions.</p>
            <Link to="/services#surgical-pathology" className="learn-more">Explore surgical pathology</Link>
          </div>
          <div className="service-item">
            <Icon name="droplet" className="card-icon" />
            <h3>Cytopathology</h3>
            <p>Analysis of cells from body fluids and fine needle aspirations, supporting early detection and diagnosis.</p>
            <Link to="/services#cytopathology" className="learn-more">Explore cytopathology</Link>
          </div>
          <div className="service-item">
            <Icon name="layers" className="card-icon" />
            <h3>Dermatopathology</h3>
            <p>Evaluation of skin biopsies, from inflammatory conditions to melanoma, for dermatologists and specialists.</p>
            <Link to="/services#dermatopathology" className="learn-more">Explore dermatopathology</Link>
          </div>
        </div>
      </section>

      <section className="immunohistochemistry">
        <div className="band-intro">
          <p className="section-label">Specialized Testing</p>
          <h2>Advanced Immunohistochemistry</h2>
          <p>Targeted antibody panels let our pathologists characterize tissue with the precision that modern treatment planning requires.</p>
        </div>
        <div className="feature-grid">
          <div className="feature-item">
            <Icon name="target" className="card-icon" />
            <h3>Precision Diagnoses</h3>
            <p>Pinpoint specific proteins within tissue samples, enabling meticulous characterization and accurate diagnoses.</p>
          </div>
          <div className="feature-item">
            <Icon name="dna" className="card-icon" />
            <h3>Biomarker Insights</h3>
            <p>Identify unique biomarkers that add depth to disease differentiation and support tailored treatment plans.</p>
          </div>
          <div className="feature-item">
            <Icon name="flag" className="card-icon" />
            <h3>Early Detection</h3>
            <p>Catch subtle cellular changes early enough to change the course of care.</p>
          </div>
        </div>
      </section>

      <section className="collaboration">
        <p className="section-label">How We Work With You</p>
        <h2>Collaborative Integration for Enhanced Insights</h2>
        <div className="feature-grid">
          <div className="feature-item">
            <Icon name="microscope" className="card-icon" />
            <h3>Expert Oversight</h3>
            <p>Our pathologists guide the immunohistochemistry process, ensuring precise antibody selection and accurate interpretation.</p>
          </div>
          <div className="feature-item">
            <Icon name="phone" className="card-icon" />
            <h3>Consultative Approach</h3>
            <p>We work alongside clinicians, with discussions that enrich the diagnostic process and lead to better patient care.</p>
          </div>
          <div className="feature-item">
            <Icon name="fileCheck" className="card-icon" />
            <h3>Informed Decisions</h3>
            <p>Results give clinicians actionable insight for treatment — the practical impact of working together.</p>
          </div>
        </div>
      </section>

      <section className="practice-callout">
        <div>
          <h2>Are You a Surgical Center or Referring Practice?</h2>
          <p>See our turnaround times, specimen logistics, and how we set up new referring accounts.</p>
        </div>
        <Link to="/referring-practices" className="cta-button">Referring Practices</Link>
      </section>

      <section className="practice-callout">
        <div>
          <h2>Histology Training Program</h2>
          <p>We are the only clinical laboratory in South Florida with a state-licensed histology training program, teaching histotechnology in our own working lab.</p>
        </div>
        <Link to="/histology-training" className="cta-button">Histology Training</Link>
      </section>

      <FAQ />

      <section className="cta">
        <p className="section-label">Get Started</p>
        <h2>Elevating Healthcare through Precise Diagnostics</h2>
        <p>You’re not just partnering with a laboratory. You’re engaging a team dedicated to raising diagnostic standards for the clinicians and patients who rely on us.</p>
        <div className="cta-buttons">
          <Link to="/contact" className="cta-button">Contact Us</Link>
          <a
            href="https://bdp.labdynamo.com"
            target="_blank"
            rel="noopener noreferrer"
            className="cta-button secondary"
          >
            Provider Portal
          </a>
        </div>
      </section>
    </div>
  );
}

export default Home;
